/**
 * Frontend User Activity Tracker - PWA-OPTIMIZED VERSION
 * 
 * Tracks user activity and page visibility to coordinate with the backend
 * adaptive exchange poller for optimal real-time performance.
 * 
 * PWA OPTIMIZATIONS APPLIED:
 * - Enhanced PWA detection with multiple fallback methods
 * - PWA-specific bootstrap events bypass 30s batching
 * - Reduced PWA focus gap from 10s to 2s
 * - PWA-aware heartbeat intervals (1min PWA, 3min mobile, 5min desktop)
 * - iOS touch event compatibility with zoom prevention
 * - Enhanced mobile touch detection (touchmove, iOS gestures)
 * - PWA-specific event batching (10s PWA, 30s regular)
 * 
 * PREVIOUS OPTIMIZATIONS:
 * - Removed spammy events (mousemove, scroll for non-PWA)
 * - Implemented event batching with PWA awareness
 * - Added proper debouncing (5s activity, 2s visibility)
 * - Optimized PWA/visibility tracking with enhanced detection
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useSSEStore } from '../store/sseStore';

// Activity tracking intervals - OPTIMIZED
const ACTIVITY_DEBOUNCE_MS = 5000; // 5 seconds (reduced frequency)
const VISIBILITY_REPORT_DEBOUNCE_MS = 2000; // 2 seconds

// Activity event types - ENHANCED for PWA with conditional touch events
const getActivityEvents = (isPWA: boolean, isMobile: boolean) => {
  const baseEvents = [
    'mousedown',
    'keypress',
    'touchstart',  // Mobile: Touch begins
    'touchend',    // Mobile: Touch ends (important for taps)
    'click',
    'focus'
  ];

  // Add PWA and mobile-specific events
  if (isPWA || isMobile) {
    return [
      ...baseEvents,
      'touchmove',     // PWA: Touch gestures (with throttling)
      'gesturestart',  // iOS: Gesture detection
      'gesturechange', // iOS: Gesture in progress
      'gestureend'     // iOS: Gesture completed
    ];
  }

  return baseEvents;
};

interface ActivityMetrics {
  lastActivity: number;
  pageVisible: boolean;
  manualRefreshCount: number;
  apiCallCount: number;
}

// Batching interface for queued events
interface QueuedEvent {
  eventType: string;
  data: any;
  timestamp: number;
}

// Export singleton for SSE store access
let trackerInstance: UserActivityTracker | null = null;

export const getUserActivityTracker = () => trackerInstance;

class UserActivityTracker {
  private lastActivityTime = Date.now();
  private isPageVisible = !document.hidden;
  private manualRefreshCount = 0;
  private apiCallCount = 0;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private activityDebounceTimeout: NodeJS.Timeout | null = null;
  private visibilityDebounceTimeout: NodeJS.Timeout | null = null;
  
  // Event batching system - NEW OPTIMIZATION
  private eventQueue: QueuedEvent[] = [];
  private batchFlushInterval: NodeJS.Timeout | null = null;
  private readonly BATCH_FLUSH_INTERVAL = 30000; // 30 seconds
  private readonly MAX_QUEUE_SIZE = 50; // Flush early if queue gets too large
  
  // Touch event throttling for PWA
  private touchMoveThrottle: NodeJS.Timeout | null = null;
  private readonly TOUCH_MOVE_THROTTLE_MS = 1000; // 1 second throttle for touchmove
  
  constructor() {
    // Set singleton instance for external access
    trackerInstance = this;
    this.initializeTracking();
    this.startBatchFlushing();
  }

  /**
   * Initialize activity and visibility tracking - ENHANCED for PWA
   */
  private initializeTracking() {
    const isPWA = this.detectPWA();
    const deviceType = this.detectDeviceType();
    const isMobile = deviceType === 'mobile';
    
    // Get activity events based on context
    const activityEvents = getActivityEvents(isPWA, isMobile);
    
    console.log(`🎯 Initializing activity tracking for ${deviceType} (PWA: ${isPWA}) with ${activityEvents.length} event types`);
    
    // Track user activity events with enhanced mobile support
    activityEvents.forEach(eventType => {
      if (eventType === 'touchmove') {
        // Special throttled handling for touchmove
        document.addEventListener(eventType, this.handleThrottledTouchMove, { passive: true });
      } else if (eventType.startsWith('gesture')) {
        // iOS gesture events
        document.addEventListener(eventType, this.handleGestureActivity, { passive: true });
      } else {
        // Standard activity events
        document.addEventListener(eventType, this.handleUserActivity, { passive: true });
      }
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // PWA-specific visibility events - ENHANCED with better detection
    if (isPWA) {
      console.log('🚀 [PWA] Detected PWA environment, enabling PWA-specific event listeners');
      // Add PWA listeners when actually in PWA mode (using enhanced detection)
      window.addEventListener('focus', this.handlePWAFocus);
      window.addEventListener('blur', this.handlePWABlur);
      
      // Add PWA-specific page lifecycle events
      window.addEventListener('pageshow', this.handlePWAPageShow);
      window.addEventListener('pagehide', this.handlePWAPageHide);
    }
    
    // Mobile app state listeners - only for mobile devices
    if (isMobile) {
      document.addEventListener('resume', this.handleAppResume, false);
      document.addEventListener('pause', this.handleAppPause, false);
    }
    
    // Start heartbeat to backend
    this.startHeartbeat();
  }

  /**
   * Handle throttled touch move events for PWA
   */
  private handleThrottledTouchMove = () => {
    if (this.touchMoveThrottle) {
      return; // Already throttling
    }

    this.touchMoveThrottle = setTimeout(() => {
      this.lastActivityTime = Date.now();
      this.queueEvent('touch_gesture', {
        type: 'touchmove',
        timestamp: Date.now(),
        isPWA: this.detectPWA()
      });
      this.touchMoveThrottle = null;
    }, this.TOUCH_MOVE_THROTTLE_MS);
  };

  /**
   * Handle iOS gesture events
   */
  private handleGestureActivity = (event: Event) => {
    this.lastActivityTime = Date.now();
    this.queueEvent('ios_gesture', {
      type: event.type,
      timestamp: Date.now(),
      isPWA: this.detectPWA(),
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent)
    });
  };
  private handleUserActivity = () => {
    if (this.activityDebounceTimeout) {
      clearTimeout(this.activityDebounceTimeout);
    }

    this.activityDebounceTimeout = setTimeout(() => {
      this.lastActivityTime = Date.now();
      this.reportActivityToBackend();
    }, ACTIVITY_DEBOUNCE_MS);
  };

  /**
   * Handle page visibility changes with debouncing - OPTIMIZED
   */
  private handleVisibilityChange = () => {
    const wasVisible = this.isPageVisible;
    const isNowVisible = !document.hidden;
    
    // Mobile recovery optimization: Immediate action for return from background
    if (!wasVisible && isNowVisible) {
      console.log('🚀 [Mobile Recovery] User returned from background');
      
      // 1. Immediate activity boost (no debounce for critical path)
      this.lastActivityTime = Date.now();
      this.sendToBackend('return_from_background', {
        timestamp: Date.now(),
        previouslyVisible: false,
        deviceType: this.detectDeviceType(),
        deviceId: this.generateDeviceId(),
        priority: 'immediate'
      });
      
      // 2. Also trigger standard debounced visibility report
      this.scheduleVisibilityReport();
    } else {
      // Standard handling for other visibility changes
      this.scheduleVisibilityReport();
    }
    
    this.isPageVisible = isNowVisible;
  };

  /**
   * Schedule debounced visibility report - NEW OPTIMIZATION
   */
  private scheduleVisibilityReport() {
    if (this.visibilityDebounceTimeout) {
      clearTimeout(this.visibilityDebounceTimeout);
    }

    this.visibilityDebounceTimeout = setTimeout(() => {
      this.reportVisibilityToBackend();
    }, VISIBILITY_REPORT_DEBOUNCE_MS);
  }

  /**
   * Detect if user is on mobile device or PWA
   */
  private detectDeviceType(): 'mobile' | 'desktop' {
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isPWA = this.detectPWA();
    return (isMobile || isPWA) ? 'mobile' : 'desktop';
  }

  /**
   * Enhanced PWA detection with multiple fallback methods
   */
  private detectPWA(): boolean {
    // Method 1: Standard CSS media query detection
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    
    // Method 2: iOS PWA detection via navigator.standalone
    if ((window.navigator as any).standalone === true) {
      return true;
    }
    
    // Method 3: Service Worker + PWA context (less restrictive than before)
    if ('serviceWorker' in navigator) {
      // Check for PWA-specific URL parameters or referrer
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('pwa') || urlParams.has('standalone')) {
        return true;
      }
    }
    
    // Method 4: Check for PWA manifest presence and display mode
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink && window.matchMedia('(display-mode: minimal-ui)').matches) {
      return true;
    }
    
    // Method 5: Android PWA detection via referrer
    if (document.referrer.includes('android-app://')) {
      return true;
    }
    
    return false;
  }

  private generateDeviceId(): string {
    // Generate a unique device identifier based on browser characteristics
    // This is a simplified fingerprint - in production you might want more sophisticated methods
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
      ctx ? ctx.getImageData(0, 0, canvas.width, canvas.height).data.slice(0, 10).join(',') : 'no-canvas'
    ].join('|');
    
    // Create a simple hash of the fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Store device ID in localStorage for consistency across sessions
    const deviceIdKey = 'trading_device_id';
    let deviceId = localStorage.getItem(deviceIdKey);
    
    if (!deviceId) {
      deviceId = `${this.detectDeviceType()}_${Math.abs(hash).toString(36)}_${Date.now().toString(36)}`;
      localStorage.setItem(deviceIdKey, deviceId);
    }
    
    return deviceId;
  }

  /**
   * Handle PWA focus events - ENHANCED with better gap detection
   */
  private handlePWAFocus = () => {
    // Reduced minimum gap requirement for PWA contexts (was 10 seconds, now 2 seconds)
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    if (timeSinceLastActivity > 2000) { // 2 seconds minimum gap for PWA
      console.log('📱 [PWA] App gained focus after inactivity');
      this.lastActivityTime = Date.now();
      
      // Send immediate PWA bootstrap event (bypasses batching)
      this.sendToBackend('pwa_bootstrap', {
        state: 'focus',
        timestamp: Date.now(),
        isPWA: true,
        priority: 'immediate',
        timeSinceLastActivity
      });
    }
  };

  /**
   * Handle PWA blur events - ENHANCED
   */
  private handlePWABlur = () => {
    console.log('📱 [PWA] App lost focus');
    this.queueEvent('pwa_state_change', {
      state: 'blur',
      timestamp: Date.now(),
      isPWA: true
    });
  };

  /**
   * Handle PWA page show events (PWA returning from background)
   */
  private handlePWAPageShow = (event: PageTransitionEvent) => {
    console.log('📱 [PWA] Page show event - app returning from background');
    this.lastActivityTime = Date.now();
    
    // Send immediate PWA bootstrap event for background return
    this.sendToBackend('pwa_bootstrap', {
      state: 'pageshow',
      timestamp: Date.now(),
      isPWA: true,
      priority: 'immediate',
      persisted: event.persisted
    });
  };

  /**
   * Handle PWA page hide events (PWA going to background)
   */
  private handlePWAPageHide = () => {
    console.log('📱 [PWA] Page hide event - app going to background');
    // Flush any pending events before going to background
    this.flushEventQueue();
    
    this.queueEvent('pwa_state_change', {
      state: 'pagehide',
      timestamp: Date.now(),
      isPWA: true
    });
  };

  /**
   * Handle mobile app resume events - OPTIMIZED
   */
  private handleAppResume = () => {
    console.log('📱 [Mobile] App resumed from background');
    this.lastActivityTime = Date.now();
    this.sendToBackend('return_from_background', {
      timestamp: Date.now(),
      deviceType: 'mobile',
      deviceId: this.generateDeviceId(),
      priority: 'immediate'
    });
  };

  /**
   * Handle mobile app pause events
   */
  private handleAppPause = () => {
    console.log('📱 [Mobile] App paused to background');
    // Flush any pending events before going to background
    this.flushEventQueue();
  };

  /**
   * Record a manual refresh action
   */
  recordManualRefresh() {
    this.manualRefreshCount++;
    this.reportManualRefreshToBackend();
  }

  /**
   * Record activity boost due to SSE reconnection
   */
  recordReconnectionActivity() {
    this.lastActivityTime = Date.now();
    this.sendToBackend('sse_reconnection_boost', {
      timestamp: Date.now(),
      deviceType: this.detectDeviceType(),
      deviceId: this.generateDeviceId(),
      priority: 'immediate'
    });
  }

  /**
   * Record an API call
   */
  recordApiCall() {
    this.apiCallCount++;
    // API calls are frequent, so batch them
    this.queueEvent('api_call', {
      timestamp: Date.now(),
      count: this.apiCallCount
    });
  }

  /**
   * Get current activity metrics
   */
  getActivityMetrics(): ActivityMetrics {
    return {
      lastActivity: this.lastActivityTime,
      pageVisible: this.isPageVisible,
      manualRefreshCount: this.manualRefreshCount,
      apiCallCount: this.apiCallCount
    };
  }

  /**
   * Start periodic heartbeat to backend - PWA-OPTIMIZED INTERVALS
   */
  private startHeartbeat() {
    const deviceType = this.detectDeviceType();
    const isPWA = this.detectPWA();
    
    // PWA-aware heartbeat intervals - PWA gets faster heartbeat due to update delay issues
    let heartbeatInterval: number;
    if (isPWA) {
      heartbeatInterval = 60000; // 1 minute for PWA (much faster)
    } else if (deviceType === 'mobile') {
      heartbeatInterval = 180000; // 3 minutes for mobile
    } else {
      heartbeatInterval = 300000; // 5 minutes for desktop
    }
    
    const intervalMinutes = Math.round(heartbeatInterval/60000);
    console.log(`💓 Starting PWA-optimized ${deviceType} heartbeat every ${intervalMinutes} minutes (isPWA: ${isPWA})`);
    
    // Send an initial heartbeat after 30 seconds
    setTimeout(() => {
      console.log('🧪 Testing initial heartbeat...');
      this.reportHeartbeatToBackend();
    }, 30000);
    
    this.heartbeatInterval = setInterval(() => {
      console.log('⏰ Heartbeat interval triggered');
      this.reportHeartbeatToBackend();
    }, heartbeatInterval);
  }

  /**
   * Report user activity to backend - NOW DEBOUNCED AND BATCHED
   */
  private reportActivityToBackend() {
    this.queueEvent('user_activity', {
      timestamp: this.lastActivityTime,
      type: 'user_interaction'
    });
  }

  /**
   * Report page visibility to backend - NOW DEBOUNCED AND BATCHED  
   */
  private reportVisibilityToBackend() {
    this.queueEvent('page_visibility', {
      visible: this.isPageVisible,
      timestamp: Date.now()
    });
  }

  /**
   * Report manual refresh to backend - NOW BATCHED
   */
  private reportManualRefreshToBackend() {
    this.queueEvent('manual_refresh', {
      timestamp: Date.now(),
      count: this.manualRefreshCount
    });
  }

  /**
   * Report heartbeat to backend - STILL IMMEDIATE (critical for adaptive polling)
   */
  private reportHeartbeatToBackend() {
    this.sendToBackend('heartbeat', {
      timestamp: Date.now(),
      deviceType: this.detectDeviceType(),
      deviceId: this.generateDeviceId(),
      metrics: this.getActivityMetrics()
    });
  }

  /**
   * Start batch flushing interval - PWA-AWARE OPTIMIZATION
   */
  private startBatchFlushing() {
    const isPWA = this.detectPWA();
    const batchInterval = isPWA ? 10000 : this.BATCH_FLUSH_INTERVAL; // 10s for PWA, 30s for regular
    
    console.log(`📦 Starting PWA-aware batching with ${batchInterval/1000}s interval (isPWA: ${isPWA})`);
    
    this.batchFlushInterval = setInterval(() => {
      this.flushEventQueue();
    }, batchInterval);
  }

  /**
   * Add event to batch queue with PWA-aware immediate sending - ENHANCED
   */
  private queueEvent(eventType: string, data: any) {
    // Enhance data with device information
    const enhancedData = {
      ...data,
      deviceType: data.deviceType || this.detectDeviceType(),
      deviceId: data.deviceId || this.generateDeviceId(),
      timestamp: data.timestamp || Date.now(),
      isPWA: data.isPWA || this.detectPWA()
    };

    // Check for PWA priority events that should bypass batching
    const isPWAPriorityEvent = this.isPWAPriorityEvent(eventType, enhancedData);
    
    if (isPWAPriorityEvent) {
      console.log(`🚀 [PWA] Sending priority event immediately: ${eventType}`);
      // Send immediately, don't queue
      this.sendToBackend(eventType, enhancedData);
      return;
    }

    const queuedEvent: QueuedEvent = {
      eventType,
      data: enhancedData,
      timestamp: Date.now()
    };

    this.eventQueue.push(queuedEvent);

    // PWA-aware batch flushing - reduce interval for PWA contexts
    const isPWA = this.detectPWA();
    const batchThreshold = isPWA ? 10 : this.MAX_QUEUE_SIZE; // Smaller threshold for PWA

    if (this.eventQueue.length >= batchThreshold) {
      console.log('🔄 Early queue flush - PWA-aware threshold reached');
      this.flushEventQueue();
    }
  }

  /**
   * Check if event should be sent immediately for PWA contexts
   */
  private isPWAPriorityEvent(eventType: string, data: any): boolean {
    // PWA bootstrap events always get priority
    if (eventType === 'pwa_bootstrap') {
      return true;
    }
    
    // Return from background events get priority
    if (eventType === 'return_from_background') {
      return true;
    }
    
    // PWA state changes with focus get priority
    if (eventType === 'pwa_state_change' && data.state === 'focus') {
      return true;
    }
    
    // Events marked with priority flag
    if (data.priority === 'immediate') {
      return true;
    }
    
    return false;
  }

  /**
   * Flush all queued events to backend - NEW OPTIMIZATION
   */
  private async flushEventQueue() {
    if (this.eventQueue.length === 0) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    console.log(`📦 Batching ${eventsToSend.length} activity events`);

    try {
      const { accessToken } = useAuthStore.getState();
      if (!accessToken) {
        console.log('Activity tracking skipped: No access token available');
        return;
      }

      // Send batched events to new batch endpoint
      fetch('/api/polling/user-activity-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          events: eventsToSend
        })
      }).catch(error => {
        console.log('Batch activity tracking failed:', error);
      });
    } catch (error) {
      console.log('Batch activity tracking error:', error);
    }
  }

  /**
   * Send data to backend via API endpoint - LEGACY METHOD (only for critical events)
   */
  private async sendToBackend(eventType: string, data: any) {
    try {
      const { accessToken } = useAuthStore.getState();
      if (!accessToken) {
        console.log('Activity tracking skipped: No access token available');
        return;
      }

      // Get client ID from SSE store
      const { clientId } = useSSEStore.getState();

      // Automatically enhance data with device information if not already present
      const enhancedData = {
        ...data,
        deviceType: data.deviceType || this.detectDeviceType(),
        deviceId: data.deviceId || this.generateDeviceId(),
        clientId: data.clientId || clientId || 'unknown', // Use SSE client ID
        timestamp: data.timestamp || Date.now()
      };

      console.log(`🚀 Sending immediate activity tracking: ${eventType}`, enhancedData);

      // Send to backend activity tracking endpoint
      fetch('/api/polling/user-activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          event_type: eventType,
          data: enhancedData
        }),
        // 🔥 FIX: Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000) // 10 second timeout
      }).catch(error => {
        // Silently fail - don't spam console with activity tracking errors
        console.log('Activity tracking failed:', error);
      });
    } catch (error) {
      // Silently fail - activity tracking is not critical
      console.log('Activity tracking error:', error);
    }
  }

  /**
   * Cleanup tracking - ENHANCED for PWA
   */
  cleanup() {
    const isPWA = this.detectPWA();
    const deviceType = this.detectDeviceType();
    const isMobile = deviceType === 'mobile';
    
    // Get activity events based on context (same as initialization)
    const activityEvents = getActivityEvents(isPWA, isMobile);
    
    // Remove event listeners
    activityEvents.forEach(eventType => {
      if (eventType === 'touchmove') {
        document.removeEventListener(eventType, this.handleThrottledTouchMove);
      } else if (eventType.startsWith('gesture')) {
        document.removeEventListener(eventType, this.handleGestureActivity);
      } else {
        document.removeEventListener(eventType, this.handleUserActivity);
      }
    });
    
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Remove PWA-specific event listeners
    if (isPWA) {
      window.removeEventListener('focus', this.handlePWAFocus);
      window.removeEventListener('blur', this.handlePWABlur);
      window.removeEventListener('pageshow', this.handlePWAPageShow);
      window.removeEventListener('pagehide', this.handlePWAPageHide);
    }
    
    // Remove mobile app state listeners
    if (isMobile) {
      document.removeEventListener('resume', this.handleAppResume);
      document.removeEventListener('pause', this.handleAppPause);
    }
    
    // Clear timeouts and intervals
    if (this.activityDebounceTimeout) {
      clearTimeout(this.activityDebounceTimeout);
    }
    if (this.visibilityDebounceTimeout) {
      clearTimeout(this.visibilityDebounceTimeout);
    }
    if (this.touchMoveThrottle) {
      clearTimeout(this.touchMoveThrottle);
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.batchFlushInterval) {
      clearInterval(this.batchFlushInterval);
    }
    
    // Final flush before cleanup
    this.flushEventQueue();
  }
}

// Global instance
const activityTracker = new UserActivityTracker();

/**
 * React hook for global activity tracking initialization (use only once in App.tsx)
 */
export const useGlobalActivityTracking = () => {
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      console.log('🎯 OPTIMIZED user activity tracking started');
    }

    return () => {
      if (mountedRef.current) {
        activityTracker.cleanup();
        console.log('👋 OPTIMIZED user activity tracking stopped');
      }
    };
  }, []);

  return {
    recordManualRefresh: () => activityTracker.recordManualRefresh(),
    recordApiCall: () => activityTracker.recordApiCall(),
    getActivityMetrics: () => activityTracker.getActivityMetrics()
  };
};

/**
 * React hook for accessing activity tracking methods (safe to use in any component)
 */
export const useUserActivityTracking = () => {
  const recordManualRefresh = useCallback(() => {
    activityTracker.recordManualRefresh();
  }, []);

  const recordApiCall = useCallback(() => {
    activityTracker.recordApiCall();
  }, []);

  const getActivityMetrics = useCallback(() => {
    return activityTracker.getActivityMetrics();
  }, []);

  return {
    recordManualRefresh,
    recordApiCall,
    getActivityMetrics
  };
};

/**
 * Conditional activity tracking hook - only activates when authenticated
 */
export const useConditionalGlobalActivityTracking = (shouldTrack: boolean) => {
  const mountedRef = useRef(false);
  const trackingActiveRef = useRef(false);

  useEffect(() => {
    if (shouldTrack && !trackingActiveRef.current) {
      if (!mountedRef.current) {
        mountedRef.current = true;
        trackingActiveRef.current = true;
        console.log('🎯 AUTHENTICATED user activity tracking started');
      }
    } else if (!shouldTrack && trackingActiveRef.current) {
      trackingActiveRef.current = false;
      console.log('🛑 User activity tracking stopped - user not authenticated');
      // Don't cleanup completely, just stop tracking
    }
  }, [shouldTrack]);

  useEffect(() => {
    return () => {
      if (mountedRef.current) {
        activityTracker.cleanup();
        console.log('👋 User activity tracking cleaned up on unmount');
      }
    };
  }, []);

  return {
    recordManualRefresh: () => shouldTrack ? activityTracker.recordManualRefresh() : undefined,
    recordApiCall: () => shouldTrack ? activityTracker.recordApiCall() : undefined,
    getActivityMetrics: () => shouldTrack ? activityTracker.getActivityMetrics() : null
  };
};

// Export singleton for manual usage
export { activityTracker };